import React from 'react'
import { motion } from 'framer-motion'
import { 
  MessageSquare, Search, Plus, TrendingUp, Users, Pin, 
  Flame, Clock, Filter, Star, Award, Calendar, Target, ArrowRight, Eye, Heart 
} from 'lucide-react'
import { Link } from 'react-router-dom'

const ForumsPage = () => {
  const [selectedCategory, setSelectedCategory] = React.useState('All')

  const categories = [
    { name: 'All', count: 156, icon: MessageSquare, color: 'from-le-accent to-purple-600' },
    { name: 'Gear Talk', count: 45, icon: Star, color: 'from-le-accent-2 to-blue-600' },
    { name: 'Live Music', count: 32, icon: Calendar, color: 'from-le-amber to-orange-600' },
    { name: 'Recording', count: 28, icon: Target, color: 'from-green-500 to-emerald-600' },
    { name: 'Venues', count: 23, icon: Users, color: 'from-pink-500 to-rose-600' },
    { name: 'Buy/Sell', count: 18, icon: TrendingUp, color: 'from-purple-500 to-indigo-600' }
  ]

  const hotTopics = [
    {
      id: 1,
      title: 'Best venues for acoustic performances in Wichita?',
      author: 'Sarah Mitchell',
      category: 'Venues',
      replies: 23,
      views: 456,
      likes: 12,
      lastActivity: '2 hours ago',
      pinned: true,
      hot: true,
      avatar: 'SM'
    },
    {
      id: 2,
      title: 'Fender vs Gibson for live rock performances - experiences?',
      author: 'Mike Thunder',
      category: 'Gear Talk',
      replies: 67,
      views: 1234,
      likes: 34,
      lastActivity: '1 hour ago',
      hot: true,
      avatar: 'MT'
    },
    {
      id: 3,
      title: 'Open mic nights - which venues are most welcoming to new artists?',
      author: 'Jessica Solo',
      category: 'Live Music',
      replies: 19,
      views: 289,
      likes: 8,
      lastActivity: '3 hours ago',
      avatar: 'JS'
    }
  ]

  const forumStats = [
    { label: 'Active Members', value: '2.5K+', icon: Users },
    { label: 'Topics This Week', value: '89', icon: TrendingUp },
    { label: 'Expert Contributors', value: '150+', icon: Award },
    { label: 'Response Time', value: '< 30min', icon: Clock }
  ]

  return (
    <div className="pt-16 lg:pt-20 min-h-screen page-transition">
      {/* Header */}
      <section className="section-padding py-16 bg-gradient-to-b from-le-darker/50 to-transparent">
        <div className="container-max">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-7xl font-display font-bold text-white mb-6">
              Community
              <span className="text-gradient block">Forums</span>
            </h1>
            <p className="text-2xl text-white/70 max-w-4xl mx-auto mb-8">
              Connect with fellow musicians, share knowledge, get advice, and discuss everything 
              music-related with Wichita's most passionate audio community.
            </p>
            
            {/* Forum Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {forumStats.map((stat, index) => {
                const Icon = stat.icon
                return (
                  <motion.div
                    key={stat.label}
                    className="glass-card p-6 text-center card-hover"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Icon size={32} className="text-le-accent mx-auto mb-3" />
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-white/60">{stat.label}</div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="section-padding py-16">
        <div className="container-max">
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl font-display font-bold text-white mb-8 text-center">
              Discussion Categories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category, index) => {
                const Icon = category.icon
                return (
                  <motion.div
                    key={category.name}
                    className="glass-card p-8 text-center card-hover group cursor-pointer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => setSelectedCategory(category.name)}
                  >
                    <div className={`w-20 h-20 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                      <Icon size={36} className="text-white" />
                    </div>
                    <h3 className="text-xl font-display font-bold text-white mb-3 group-hover:text-le-accent transition-colors duration-300">
                      {category.name}
                    </h3>
                    <div className="text-3xl font-bold text-le-accent mb-2">{category.count}</div>
                    <p className="text-white/60">active topics</p>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>

          {/* Hot Topics Preview */}
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-display font-bold text-white flex items-center space-x-3">
                <Flame className="text-red-500" size={32} />
                <span>Hot Topics</span>
              </h2>
              <button className="btn-secondary flex items-center space-x-2">
                <Plus size={18} />
                <span>Start Discussion</span>
              </button>
            </div>

            <div className="space-y-6">
              {hotTopics.map((topic, index) => (
                <motion.div
                  key={topic.id}
                  className="glass-card p-8 card-hover group"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start space-x-6">
                    {/* Avatar */}
                    <div className="w-16 h-16 bg-gradient-to-br from-le-accent to-le-accent-2 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-lg">{topic.avatar}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          {topic.pinned && (
                            <Pin size={16} className="text-le-accent" />
                          )}
                          {topic.hot && (
                            <Flame size={16} className="text-red-500" />
                          )}
                          <span className="bg-le-accent/20 text-le-accent text-xs px-3 py-1 rounded-full font-medium">
                            {topic.category}
                          </span>
                        </div>
                        <span className="text-white/50 text-sm">{topic.lastActivity}</span>
                      </div>

                      <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-le-accent transition-colors duration-300 leading-tight">
                        {topic.title}
                      </h3>

                      <div className="flex items-center space-x-2 mb-4">
                        <span className="text-white/70">by</span>
                        <span className="font-medium text-white">{topic.author}</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-6 text-sm text-white/60">
                          <div className="flex items-center space-x-2"><MessageSquare size={16} /><span>{topic.replies} replies</span></div>
                          <div className="flex items-center space-x-2"><Eye size={16} /><span>{topic.views} views</span></div>
                          <div className="flex items-center space-x-2"><Heart size={16} /><span>{topic.likes} likes</span></div>
                        </div>
                        <Link to={`/thread/${topic.id}`} className="text-le-accent hover:text-le-accent-2 font-medium transition-colors duration-300 flex items-center space-x-2">
                          <span>Join Discussion</span>
                          <ArrowRight size={16} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="glass-card p-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-le-accent/10 via-transparent to-le-accent-2/10"></div>
              <div className="relative z-10">
                <MessageSquare size={80} className="text-le-accent mx-auto mb-8 animate-pulse-glow" />
                <h2 className="text-4xl font-display font-bold text-white mb-6">
                  Forums Launching Soon
                </h2>
                <p className="text-xl text-white/70 mb-8 max-w-3xl mx-auto leading-relaxed">
                  Join thousands of musicians, audio engineers, venue owners, and music lovers 
                  in meaningful discussions about gear, techniques, venues, and the local scene.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <button className="btn-primary text-xl px-12 py-6 flex items-center space-x-3">
                    <Users size={24} />
                    <span>Join the Community</span>
                  </button>
                  <button className="btn-accent text-xl px-12 py-6 flex items-center space-x-3">
                    <Plus size={24} />
                    <span>Start First Topic</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default ForumsPage